// Copyright (c) 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import XLSX from "xlsx";

import {
  ReviewState,
  Uid,
  Activity,
  Volunteer,
} from "../store/schema";
import { throwIfNull, durationString, createOuts } from "../util";
import { CoordinatorState } from "./api";
import { logCrash } from "./crash";

const { debug } = createOuts("XlsxReporter", true);

export async function downloadAll(state: CoordinatorState) {
  debug(`XlsxReporter.downloadAll`, state);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, volunteerSheet(state), "Volunteers");
  XLSX.utils.book_append_sheet(workbook, activitySheet(state), "Activities");
  XLSX.writeFile(workbook, "volunteer-rep.xlsx");
}

function activitySheet(state: CoordinatorState) {
  return XLSX.utils.aoa_to_sheet(
    [
      activityTitleRow(),
      ...[...state.actIndex.values()]
        .map(act => activityRow(state, act))
        .filter(row => row != null)
        .map(row => throwIfNull(row))
    ]
  );
}

function activityRow(state: CoordinatorState, act: Activity): any[] | null {
  debug(`XlsxReporter.activityRow(${act.uid})`);

  const cat = state.catIndex.get(act.categoryUid);
  if (cat == null) {
    logCrash(new Error("downloadAll: category not found"), act);
    return null;
  }

  const org = state.orgIndex.get(act.organizationUid);
  if (org == null) {
    logCrash(new Error("downloadAll: organization not found"), act);
    return null;
  }

  const sup = state.supIndex.get(act.supervisorUid);
  if (sup == null) {
    logCrash(new Error("downloadAll: supervisor not found"), act);
    return null;
  }

  const vol = state.volIndex.get(act.volunteerUid);
  if (vol == null) {
    logCrash(new Error("downloadAll: volunteer not found"), act);
    return null;
  }

  return [
    vol.name,
    vol.email,
    vol.phone,
    vol.classYear,
    org.name,
    sup.name,
    sup.email,
    act.startDate,
    act.endDate,
    durationString(act.durationInSec),
    cat.name,
    act.description,
  ];
}

function activityTitleRow(): string[] {
  return [
    "Name",
    "Email",
    "Phone",
    "Class Year",
    "Organization",
    "Supervisor Name",
    "Supervisor Email",
    "Start Date",
    "End Date",
    "Duration",
    "Category",
    "Description",
  ]
}

function volunteerSheet(state: CoordinatorState) {
  return XLSX.utils.aoa_to_sheet(
    [
      volunteerTitleRow(),
      ...[...state.volIndex.entries()]
        .filter(([uid, vol]) => uid === vol.email)
        .map(([_, vol]) => vol)
        .map(vol => volunteerRow(state, vol))
        .filter(row => row != null)
    ]
  );
}

function volunteerRow(state: CoordinatorState, vol: Volunteer): any[] {
  const approvedActivities: Uid[] = state.activityReviews
    .filter(x => x.data.state === ReviewState.Approved)
    .map(x => x.data.uid);
  const volunteerActivities = [...state.actIndex.values()]
    .filter(x => !x.isDeleted && (x.volunteerUid === vol.uid || x.volunteerUid === vol.email));
  const totalSeconds = volunteerActivities
    .reduce((acc, x) => acc + x.durationInSec, 0);
  const approvedSeconds = volunteerActivities
    .filter(x => approvedActivities.some(uid => uid === x.uid))
    .reduce((acc, x) => acc + x.durationInSec, 0);

  return [
    vol.name,
    vol.preferredName,
    vol.email,
    vol.phone,
    vol.classYear,
    durationString(approvedSeconds),
    durationString(totalSeconds),
  ];
}

function volunteerTitleRow(): string[] {
  return [
    "Name",
    "Preferred Name",
    "Email",
    "Phone",
    "Class Year",
    "Approved Duration",
    "Total Duration",
  ];
}